'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { Splide, SplideTrack, SplideSlide } from '@splidejs/react-splide'
import { Gallery } from 'react-photoswipe-gallery'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import Typography from 'src/components/design-system/Typography'
import ArrowSliderHomePrev from 'public/icons/arrows/arrow-circle-left.svg'
import ArrowSliderHomeNext from 'public/icons/arrows/arrow-circle-right.svg'
import { GalleryItem } from 'src/components/items/GalleryItem'
import { getLocalizedMedia, getLocalizedThumb } from 'src/_helpers/getLocalizedThumb'

export default function ImageCarousel({ block, locale }) {
  const translateEntry = useTranslateEntry()
  const splideRef = useRef(null)

  const mainTitle = block.translations?.[locale]?.title
  const images = useMemo(() => (Array.isArray(block?.files) ? block.files : []), [block?.files])
  const hasSlides = images.length > 0
  const [pages, setPages] = useState(() => ({
    current: hasSlides ? 1 : 0,
    total: hasSlides ? Math.max(1, Math.ceil(images.length / 3)) : 1,
  }))

  useEffect(() => {
    if (!hasSlides) {
      setPages({ current: 0, total: 0 })
    } else {
      setPages((prev) => ({
        current: prev.current || 1,
        total: Math.max(1, prev.total || Math.ceil(images.length / 3)),
      }))
    }
  }, [hasSlides, images.length])

  const updatePages = (splide, nextIndex) => {
    if (!splide) return
    const controller = splide.Components?.Controller
    const slides = splide.length ?? images.length
    if (!controller || !slides) return
    const lastIndex = Math.max(0, slides - 1)
    const total = Math.max(1, controller.toPage(lastIndex) + 1)
    const activeIndex = typeof nextIndex === 'number' ? nextIndex : typeof splide.index === 'number' ? splide.index : 0
    const current = Math.min(total, controller.toPage(activeIndex) + 1)
    setPages({ current, total })
  }

  const handleMove = (splide, newIndex) => updatePages(splide, newIndex)
  const handleMoved = (splide, newIndex) => updatePages(splide, newIndex)
  const handleMounted = (splide) => updatePages(splide)

  const handlePrev = () => {
    splideRef.current?.go('<')
  }

  const handleNext = () => {
    splideRef.current?.go('>')
  }

  const galleryOptions = {
    arrowPrev: true,
    arrowNext: true,
    zoom: true,
    close: true,
    counter: true,
    bgOpacity: 0.95,
  }

  return (
    <section className={`bg-basic-white ${block.type.value}`} id={`${block.uuid}`}>
      <div className="mx-auto flex w-full max-w-screen-2xl flex-col gap-48 px-16 py-80 d:px-40">
        {mainTitle && (
          <Typography variant="h2-bold" color="text-dark-blue-dark-blue1">
            {mainTitle}
          </Typography>
        )}

        {hasSlides && (
          <Gallery withCaption options={galleryOptions}>
            <Splide
              ref={splideRef}
              className="splide w-full min-w-0"
              hasTrack={false}
              aria-label={`${translateEntry('T_SLIDER_ELEMENTS')} - ${mainTitle}`}
              options={{
                mediaQuery: 'min',
                rewind: true,
                pagination: false,
                updateOnMove: true,
                arrows: false,
                easing: 'cubic-bezier(0.49, 0.025, 0.49, 1)',
                gap: '1.5rem',
                padding: { right: '6rem' },
                breakpoints: {
                  768: {
                    perPage: 2,
                    padding: { right: '7rem' },
                    gap: '2rem',
                  },
                  1440: {
                    perPage: 3,
                    padding: { right: '8rem' },
                    gap: '2.5rem',
                  },
                },
              }}
              onMove={handleMove}
              onMoved={handleMoved}
              onMounted={handleMounted}
              onResized={handleMounted}
            >
              <SplideTrack className="splide__track">
                {images.map((item, index) => {
                  const translation = item?.translations?.[locale] || {}
                  const title = translation?.title
                  const alt = translation?.alt || title || ' '
                  const media = getLocalizedMedia(item, locale)
                  const thumb = getLocalizedThumb(item, 'gallery', locale)

                  if (!media || !thumb) return null

                  return (
                    <SplideSlide key={`${item?.id || index}-${thumb.url}`}>
                      <GalleryItem
                        photo={media}
                        thumb={thumb}
                        alt={alt}
                        title={title}
                        captionVariant="dot"
                        ariaLabel={translateEntry('T_OPEN_IMAGE_IN_LIGHTBOX') || title || alt}
                      />
                    </SplideSlide>
                  )
                })}
              </SplideTrack>
            </Splide>
          </Gallery>
        )}

        {hasSlides && (
          <div className="flex flex-wrap items-center gap-24 xl:max-w-[750px] max-w-665 mx-auto w-full">
            <div className="flex items-center gap-8">
              <button
                className="cursor-pointer rounded-full border-transparent border hover:border-current transition-colors duration-300"
                onClick={handlePrev}
                aria-label={translateEntry('T_SLIDER_PREV')}
                type="button"
              >
                <ArrowSliderHomePrev />
              </button>
              <span className="font-serif text-buttons-l leading-111">
                {pages.current || 0}/{pages.total || 0}
              </span>
            </div>

            <div className="relative h-12 min-w-112 w-full flex-1 overflow-hidden border-b border-b-dark-blue-dark-blue4">
              <div
                className="absolute inset-y-0 left-0 bg-style1 transition-[width] duration-500"
                style={{ width: `${pages.total ? Math.min(100, (pages.current / pages.total) * 100) : 0}%` }}
              />
            </div>

            <button
              className="cursor-pointer rounded-full border-transparent border hover:border-current transition-colors duration-300"
              onClick={handleNext}
              aria-label={translateEntry('T_SLIDER_NEXT')}
              type="button"
            >
              <ArrowSliderHomeNext />
            </button>
          </div>
        )}
      </div>
    </section>
  )
}
